import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  Search, Music, FolderOpen, ListMusic, MessageCircle, Loader2, ArrowRight,
} from 'lucide-react';
import { API } from '@/App';

/**
 * Global command palette (⌘K / Ctrl+K).
 *
 * Unified search across tracks, crates, playlists, and past chats — uses
 * `GET /api/search?q=` and routes each result to its detail page (or, for
 * chats, swaps the active right-rail session).
 */
export default function CommandPalette({ onSwitchChatSession }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [activeIdx, setActiveIdx] = useState(0);
  const inputRef = useRef(null);
  const navigate = useNavigate();

  // Global hotkey ⌘K / Ctrl+K (and Esc to close) + custom event from header
  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && (e.key === 'k' || e.key === 'K')) {
        e.preventDefault();
        setOpen((o) => !o);
      } else if (e.key === 'Escape' && open) {
        setOpen(false);
      }
    };
    const onOpen = () => setOpen(true);
    window.addEventListener('keydown', onKey);
    window.addEventListener('cratelab:open-palette', onOpen);
    return () => {
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('cratelab:open-palette', onOpen);
    };
  }, [open]);

  // Focus input on open
  useEffect(() => {
    if (open) {
      const t = setTimeout(() => inputRef.current?.focus(), 40);
      return () => clearTimeout(t);
    } else {
      setQ('');
      setResults(null);
      setActiveIdx(0);
    }
  }, [open]);

  // Debounced search
  useEffect(() => {
    if (!open || q.trim().length < 2) {
      setResults(null);
      return;
    }
    let cancelled = false;
    setLoading(true);
    const t = setTimeout(async () => {
      try {
        const { data } = await axios.get(`${API}/search`, { params: { q } });
        if (!cancelled) {
          setResults(data?.results || null);
          setActiveIdx(0);
        }
      } catch {
        if (!cancelled) setResults(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }, 200);
    return () => { cancelled = true; clearTimeout(t); };
  }, [q, open]);

  // Flatten results into a single navigable array (for keyboard nav)
  const flat = React.useMemo(() => {
    if (!results) return [];
    return [
      ...(results.tracks || []),
      ...(results.crates || []),
      ...(results.playlists || []),
      ...(results.chats || []),
    ];
  }, [results]);

  const choose = useCallback((item) => {
    if (!item) return;
    setOpen(false);
    if (item.type === 'chat' && onSwitchChatSession) {
      onSwitchChatSession(item.id);
    } else if (item.href) {
      navigate(item.href);
    }
  }, [navigate, onSwitchChatSession]);

  const onInputKey = (e) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIdx((i) => Math.min(i + 1, flat.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIdx((i) => Math.max(i - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      choose(flat[activeIdx]);
    }
  };

  if (!open) return null;

  return (
    <div
      data-testid="cmd-palette-backdrop"
      className="fixed inset-0 z-[100] flex items-start justify-center pt-[12vh] bg-black/30 backdrop-blur-sm"
      onClick={() => setOpen(false)}
    >
      <div
        data-testid="cmd-palette"
        onClick={(e) => e.stopPropagation()}
        className="w-[640px] max-w-[92vw] bg-white rounded-2xl shadow-[0_24px_70px_rgba(15,23,42,0.18)] border border-[#E2E8F0] overflow-hidden"
      >
        {/* Input */}
        <div className="flex items-center gap-3 px-4 py-3 border-b border-[#E2E8F0]">
          <Search className="w-4 h-4 text-[#94A3B8] shrink-0" />
          <input
            ref={inputRef}
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={onInputKey}
            placeholder="Search tracks, crates, playlists, past chats…"
            data-testid="cmd-palette-input"
            className="flex-1 outline-none text-sm placeholder:text-[#94A3B8] text-[#0F172A]"
          />
          {loading && <Loader2 className="w-3.5 h-3.5 text-[#2563FF] animate-spin shrink-0" />}
          <kbd className="hidden sm:inline px-1.5 py-0.5 rounded border border-[#E2E8F0] text-[10px] font-mono text-[#94A3B8]">
            ESC
          </kbd>
        </div>

        {/* Empty state */}
        {!q && (
          <div className="px-6 py-10 text-center text-xs text-[#94A3B8]">
            Type at least 2 characters to search.
            <div className="mt-3 flex items-center justify-center gap-3 text-[10px] font-mono">
              <span><kbd className="px-1 border border-[#E2E8F0] rounded">↑</kbd> <kbd className="px-1 border border-[#E2E8F0] rounded">↓</kbd> navigate</span>
              <span><kbd className="px-1 border border-[#E2E8F0] rounded">↵</kbd> open</span>
            </div>
          </div>
        )}

        {q && !loading && flat.length === 0 && (
          <div className="px-6 py-8 text-center text-xs text-[#94A3B8]">
            No results for &ldquo;{q}&rdquo;.
          </div>
        )}

        {/* Results */}
        {flat.length > 0 && (
          <div className="max-h-[60vh] overflow-y-auto py-1" data-testid="cmd-palette-results">
            <SearchGroup
              label="Tracks"
              icon={Music}
              items={results.tracks}
              startIdx={0}
              activeIdx={activeIdx}
              onPick={choose}
            />
            <SearchGroup
              label="Crates"
              icon={FolderOpen}
              items={results.crates}
              startIdx={(results.tracks || []).length}
              activeIdx={activeIdx}
              onPick={choose}
            />
            <SearchGroup
              label="Playlists"
              icon={ListMusic}
              items={results.playlists}
              startIdx={(results.tracks || []).length + (results.crates || []).length}
              activeIdx={activeIdx}
              onPick={choose}
            />
            <SearchGroup
              label="Past chats"
              icon={MessageCircle}
              items={results.chats}
              startIdx={
                (results.tracks || []).length
                + (results.crates || []).length
                + (results.playlists || []).length
              }
              activeIdx={activeIdx}
              onPick={choose}
            />
          </div>
        )}
      </div>
    </div>
  );
}

function SearchGroup({ label, icon: Icon, items, startIdx, activeIdx, onPick }) {
  if (!items || items.length === 0) return null;
  return (
    <div className="py-1">
      <p className="px-4 pt-2 pb-1 font-mono text-[10px] tracking-[0.18em] text-[#94A3B8]">
        {label.toUpperCase()}
      </p>
      <ul>
        {items.map((it, i) => {
          const idx = startIdx + i;
          const active = idx === activeIdx;
          return (
            <li key={`${it.type}-${it.id}`}>
              <button
                onClick={() => onPick(it)}
                data-testid={`cmd-palette-item-${it.type}-${it.id}`}
                className={`w-full flex items-center gap-3 px-4 py-2 text-left transition-colors ${
                  active ? 'bg-[#EFF6FF]' : 'hover:bg-[#F8FAFC]'
                }`}
              >
                <Icon className={`w-4 h-4 shrink-0 ${active ? 'text-[#2563FF]' : 'text-[#94A3B8]'}`} />
                <div className="flex-1 min-w-0">
                  <p className={`text-[15px] font-normal truncate ${active ? 'text-[#2563FF]' : 'text-[#3B82F6]'}`}>
                    {it.title}
                  </p>
                  {it.subtitle && (
                    <p className="text-[11px] text-[#64748B] truncate font-mono">{it.subtitle}</p>
                  )}
                </div>
                <ArrowRight className={`w-3 h-3 shrink-0 ${active ? 'text-[#2563FF]' : 'text-[#CBD5E1]'}`} />
              </button>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
